// import { AccountBalanceQuery, AccountId, Client, Hbar } from "@hashgraph/sdk";
// import { useQuery } from "@tanstack/react-query";

// export function useHederaBalance({
//   accountId,
//   client,
//   wcTopic,
//   hbarxId,
// }: {
//   accountId: AccountId | null;
//   client: Client | null;
//   wcTopic: string | null;
//   hbarxId: string;
// }) {
//   return useQuery<
//     { hbar: Hbar; hbarx: BigNumber },
//     Error,
//     { hbar: Hbar; hbarx: BigNumber },
//     [
//       string,
//       string,
//       string,
//       string,
//       { accountId: string | undefined; wcTopic: string | null }
//     ]
//   >({
//     queryKey: [
//       "dapp-sdk",
//       "hedera",
//       "hashgraph",
//       "balance",
//       { accountId: accountId?.toString(), wcTopic },
//     ],
//     queryFn: async () => {
//       if (!accountId || !client) throw new Error("Wallet not connected");
//       const result = await new AccountBalanceQuery()
//         .setAccountId(accountId)
//         .execute(client);

//       const hbarxBal = result.tokens?.get(hbarxId)?.toNumber() ?? 0;
//       const hbarxDec = result.tokenDecimals?.get(hbarxId) ?? 1;

//       let hbarx = new BigNumber(hbarxBal.toString());
//       hbarx = hbarx.div(new BigNumber(10).pow(hbarxDec));

//       return {
//         hbar: result.hbars,
//         hbarx,
//       };
//     },
//     enabled: !!accountId && !!client && !!wcTopic,
//     staleTime: 5 * 60 * 1000,
//     refetchInterval: 5 * 60 * 1000,
//   });
// }

import { AccountId, Hbar } from "@hashgraph/sdk";
import { useQuery } from "@tanstack/react-query";
import { BigNumber } from "bignumber.js";

const mirrorNodeUrl = "https://mainnet.mirrornode.hedera.com/api/v1";

interface AccountBalanceResponse {
  accounts: Array<{
    account: string;
    balance: {
      balance: number;
      timestamp: string;
    };
  }>;
}

interface TokenDetailsResponse {
  tokens: Array<{
    automatic_association: boolean;
    balance: number;
    created_timestamp: string;
    decimals: number;
    token_id: string;
    freeze_status: string;
    kyc_status: string;
  }>;
}

export function useHederaBalance({
  accountId,
  client,
  wcTopic,
  hbarxId,
}: {
  accountId: AccountId | null;
  client: any; // Keep for compatibility but not used
  wcTopic: string | null;
  hbarxId: string;
}) {
  return useQuery<
    { hbar: Hbar; hbarx: BigNumber },
    Error,
    { hbar: Hbar; hbarx: BigNumber },
    [
      string,
      string,
      string,
      string,
      { accountId: string | undefined; wcTopic: string | null }
    ]
  >({
    queryKey: [
      "dapp-sdk",
      "hedera",
      "hashgraph",
      "balance",
      { accountId: accountId?.toString(), wcTopic },
    ],

    queryFn: async () => {
      if (!accountId) throw new Error("Wallet not connected");

      const accountString = accountId.toString();

      try {
        // Fetch both account balance and HBARX balance in parallel
        const [accountResponse, hbarxResponse] = await Promise.all([
          fetch(
            `${mirrorNodeUrl}/accounts?account.id=${accountString}&balance=true&limit=1&order=desc`
          ),
          fetch(
            `${mirrorNodeUrl}/accounts/${accountString}/tokens?limit=2&order=desc&token.id=${hbarxId}`
          ),
        ]);

        if (!accountResponse.ok) {
          throw new Error(
            `Account balance HTTP error! status: ${accountResponse.status}`
          );
        }

        const accountData =
          (await accountResponse.json()) as AccountBalanceResponse;

        if (!accountData.accounts || accountData.accounts.length === 0) {
          console.log(`No account found for ID: ${accountString}`);
          return {
            hbar: new Hbar(0),
            hbarx: new BigNumber(0),
          };
        }

        const { balance: balanceInfo } = accountData.accounts[0];

        // Convert tinybars to HBAR (1 HBAR = 100,000,000 tinybars)
        const hbarBalance = balanceInfo.balance / 100_000_000;

        // Process HBARX balance
        let hbarxBalance = 0;
        if (hbarxResponse.ok) {
          const hbarxData =
            (await hbarxResponse.json()) as TokenDetailsResponse;

          if (hbarxData.tokens && hbarxData.tokens.length > 0) {
            const hbarxToken = hbarxData.tokens[0];
            // Convert balance using decimals
            hbarxBalance =
              hbarxToken.balance / Math.pow(10, hbarxToken.decimals);
          }
        } else {
          console.warn(
            `HBARX balance HTTP error! status: ${hbarxResponse.status}`
          );
        }

        return {
          hbar: new Hbar(hbarBalance),
          hbarx: new BigNumber(hbarxBalance),
        };
      } catch (error) {
        return {
          hbar: new Hbar(0),
          hbarx: new BigNumber(0),
        };
      }
    },
    enabled: !!accountId && !!wcTopic,
    staleTime: 5 * 60 * 1000,
    refetchInterval: 5 * 60 * 1000,
  });
}
